import * as React from 'react';
import { memo, useEffect } from 'react';

import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { camelToKebab } from '../../../Scripts/Spotlight/Components/CamelToKebab';
import { COLOR, FONT_SIZE, SPACING } from '../../packages/constants';
import { SaveCallbackFn } from './atoms/account-cell-input/account-cell-input';
import { AccountCell } from './atoms/account-cell/account-cell';
import { AccountRow } from './atoms/account-row/account-row';
import { AccountsList } from './atoms/accounts-list/accounts-list';
import { AccountsPanel } from './atoms/accounts-panel/accounts-panel';
import {
    DataGridScroller,
    StyledDataGrid,
    StyledDataGridInner,
    StyledScrollbar,
    StyledScrollbarWrapper,
} from './atoms/data-grid-scroller/data-grid-scroller';
import { FYTotalColumn } from './atoms/fy-total-column/fy-total-column';
import { HeaderCell } from './atoms/header-cell/header-cell';
import { DataGridHeader } from './atoms/header/header';
import { StyledChevronLeft, StyledChevronRight } from './atoms/icons/icons';
import { StyledTable, StyledFloatTable, AccountHeader, FloatHead, FloatTableHead } from './atoms/table/table';

dayjs.extend(isSameOrAfter);

export interface AccountChildrenType {
    id: string;
    name: string;
    displayName?: string | null;
    reportCode?: string | null;
    type?: string | null;
    children?: AccountChildrenType[] | null;
    isEliminated?: boolean;
    canEliminate?: boolean;
}

export interface AccountType {
    id: string;
    name: string;
    displayName: string | null;
    reportCode: string | null;
    type: string;
    children: AccountChildrenType[];
    isEliminated?: boolean;
    canEliminate?: boolean;
}

export interface DataGridData {
    organisation: {
        id: string;
        name: string;
        currency: string;
        financialYearStartMonth: number;
        startDate: string;
        endDate: string;
        lastActualsDate: string;
    };
    accounts: AccountType[];
    data: MonthlyDataGroupedByKey;
    bandType: string;
    mode: string;
    isGroup: boolean;
}

/**
 * Columns that appear after the accounts list in a toggleable details pane
 */
export enum DetailsColumns {
    ReportCode = 'REPORTCODE',
    DisplayName = 'DISPLAYNAME',
}

/**
 * Columns that appear in front of the accounts list
 */
export enum AccountColumns {
    Elimination = 'ELIMINATION',
}

/**
 * Columns that appear on the grid (aligned to the end)
 */
export enum GridColumns {
    EndOfFinancialYearTotal = 'EOFYTOTAL',
}
export interface DataGridProps {
    data: DataGridData;
    /**
     * Data Test ID
     */
    dataId: string;
    /**
     * External function to update displayName
     */
    displayNameChange?: SaveCallbackFn;
    /**
     * Choose what columns to show in the detailsColumns
     */
    detailsColumns?: DetailsColumns[];
    /**
     * Option to hide the EOFY column
     */
    gridColumns?: GridColumns[];
    /**
     * Expand/Collapse all rows
     */
    expansionMode?: ExpansionMode;
    /**
     * Change EOFY calculation method for Balance Sheet
     */
    isBalanceSheet?: boolean;
    /**
     * Display the eliminated icon on eliminated account rows
     */
    identifyEliminatedAccounts?: boolean;
    /**
     * Can enter mode to edit eliminations
     */
    hasEliminationsMode?: boolean;
    /**
     * Can adjust AR and AP
     */
    hasArapAdjustments?: boolean;
    themeProvider?: {};
    /**
     * External function to dispatch actions to
     */
    actionHandler?: <TType, TPayload, TResponse>(action: Action<TType, TPayload>) => Promise<void | TResponse>;
}

interface Action<TType, TPayload> {
    type: TType;
    payload?: TPayload;
}

interface MonthlyDataPoint {
    date: string;
    value: number;
}

interface MonthlyDataGroupedByKey {
    [key: string]: MonthlyDataPoint[];
}

interface MonthlyDataValueLookup {
    [k: string]: number;
}

interface GetCellValueProps {
    isGroup?: boolean;
    month: Dayjs;
    accountId: string;
    actualsToDate: Dayjs;
    monthlyData: MonthlyDataValueLookup;
    accountName: string;
    dataId: string;
}

/**
 * Different modes control which accounts should be expanded
 * In future this might have several modes for having different
 * levels of nesting or account types expanded or not
 */
export enum ExpansionMode {
    collapsed = 'COLLAPSED', // collapses all accounts
    expanded = 'EXPANDED', // expands all accounts
}

/**
 * Actions dispatched to an external handler
 */
export enum DataGridExternalActions {
    FETCH_DATA = 'FETCH_DATA',
    SAVE_ELIMINATIONS = 'SAVE_ELIMINATIONS',
    ADJUST_ARAP = 'ADJUST_ARAP',
}

/*
 * Accounts Eliminations
 */

export type EliminationsSettings = string[];

export enum AccountsEliminationsActionTypes {
    TOGGLE_ELIMINATION = 'TOGGLE_ELIMINATION',
    RESET_SETTINGS = 'RESET_SETTINGS',
}

export interface AccountsEliminationsState {
    eliminationsSettings: EliminationsSettings;
    indeterminateEliminations: string[];
}

interface AccountsEliminationsResetSettingsAction {
    type: AccountsEliminationsActionTypes.RESET_SETTINGS;
    payload: {
        eliminationsSettings: EliminationsSettings;
        indeterminateEliminations: string[];
    };
}

interface AccountsEliminationsToggleEliminationAction {
    type: AccountsEliminationsActionTypes.TOGGLE_ELIMINATION;
    account: AccountType | AccountChildrenType;
    parentAccount?: AccountType;
}

export type AccountsEliminationsActions =
    | AccountsEliminationsResetSettingsAction
    | AccountsEliminationsToggleEliminationAction;

/**
 * Maps accounts that are eliminated to an array.
 * Also determines accounts are eliminated if their children are all eliminated.
 * Assumes all children are returned necessary to determine the parent is eliminated.
 */
const getEliminatedAccounts = (accounts: AccountType[]) => {
    const eliminatedAccounts: string[] = [];
    accounts.forEach((account) => {
        if (account.isEliminated) {
            eliminatedAccounts.push(account.id);
        }
        if (account.children && account.children.length > 0) {
            // filter to get eliminated child accounts
            const eliminatedChildren = account.children.filter((item) => {
                return item.isEliminated;
            });
            const eliminatedChildrenIds = eliminatedChildren.map((item) => {
                return item.id;
            });
            /*
             * check if the length of the set matches account children length
             * if it does, push the account to eliminatedAccounts array
             */
            if (eliminatedChildren.length === account.children.length) {
                eliminatedAccounts.push(account.id);
            }
            // push the filtered set of child accounts to eliminatedAccounts array
            eliminatedAccounts.push(...eliminatedChildrenIds);
        }
    });
    return eliminatedAccounts;
};

const getIndeterminateAccounts = (accounts: AccountType[]) => {
    const indeterminateAccounts = accounts.filter((account) => {
        // set indeterminate if not all children are checked/unchecked
        const checkedChildAccounts = account.children.filter((item: AccountChildrenType) => {
            return item.isEliminated;
        });
        if (checkedChildAccounts.length !== 0 && checkedChildAccounts.length !== account.children.length) {
            return true;
        }
        return false;
    });
    const indeterminateAccountIds: string[] = indeterminateAccounts.map((item) => item.id);
    return indeterminateAccountIds;
};

export const accountsEliminationsReducer = (
    state: AccountsEliminationsState,
    action: AccountsEliminationsActions,
): AccountsEliminationsState => {
    const handleUpdate = ({
        account,
        parentAccount,
    }: {
        account: AccountType | AccountChildrenType;
        parentAccount?: AccountType;
    }) => {
        // toggle state
        // push to eliminationsSettings array
        const eliminationsSettingsCopy = [...state.eliminationsSettings];
        const indeterminateEliminationsCopy = [...state.indeterminateEliminations];
        const matchedIndex = eliminationsSettingsCopy.indexOf(account.id);
        const isAdding = matchedIndex < 0;

        if (isAdding) {
            eliminationsSettingsCopy.push(account.id);
        } else {
            eliminationsSettingsCopy.splice(matchedIndex, 1);
        }

        // remove this account from indeterminate state
        const indeterminateAccountIndex = indeterminateEliminationsCopy.indexOf(account.id);
        if (indeterminateAccountIndex >= 0) {
            indeterminateEliminationsCopy.splice(indeterminateAccountIndex, 1);
        }

        if (typeof parentAccount !== 'undefined') {
            // remove parent from indeterminate state
            const indeterminateParentAccountIndex = indeterminateEliminationsCopy.indexOf(parentAccount.id);
            if (indeterminateParentAccountIndex >= 0) {
                indeterminateEliminationsCopy.splice(indeterminateParentAccountIndex, 1);
            }
            //
            if (isAdding) {
                // set indeterminate if not all children are checked
                const checkedItems = parentAccount.children.filter((item: any) => {
                    const isItemChecked = eliminationsSettingsCopy.includes(item.id);
                    return isItemChecked;
                });
                if (
                    checkedItems.length < parentAccount.children.length &&
                    !indeterminateEliminationsCopy.includes(parentAccount.id)
                ) {
                    indeterminateEliminationsCopy.push(parentAccount.id);
                } else {
                    // check if all children are checked
                    if (!eliminationsSettingsCopy.includes(parentAccount.id)) {
                        eliminationsSettingsCopy.push(parentAccount.id);
                    }
                }
            } else {
                // uncheck
                const parentAccountMatchedIndex = eliminationsSettingsCopy.indexOf(parentAccount.id);
                if (parentAccountMatchedIndex >= 0) {
                    eliminationsSettingsCopy.splice(parentAccountMatchedIndex, 1);
                }
                // set indeterminate if not all children are unchecked
                const distinct = parentAccount.children.filter((item: any) => {
                    const isItemChecked = eliminationsSettingsCopy.includes(item.id);
                    return isItemChecked;
                });
                if (distinct.length > 0 && !indeterminateEliminationsCopy.includes(parentAccount.id)) {
                    indeterminateEliminationsCopy.push(parentAccount.id);
                }
            }
        }
        if (account.children) {
            // toggle children
            account.children.forEach((childAccount: any) => {
                const childMatchedIndex = eliminationsSettingsCopy.indexOf(childAccount.id);
                const alreadyHasChild = childMatchedIndex >= 0;
                if (isAdding && !alreadyHasChild) {
                    eliminationsSettingsCopy.push(childAccount.id);
                } else if (!isAdding && alreadyHasChild) {
                    eliminationsSettingsCopy.splice(childMatchedIndex, 1);
                }
            });
        }

        return {
            eliminationsSettings: eliminationsSettingsCopy,
            indeterminateEliminations: indeterminateEliminationsCopy,
        };
    };

    switch (action.type) {
        case AccountsEliminationsActionTypes.TOGGLE_ELIMINATION: {
            const newState = handleUpdate({ account: action.account, parentAccount: action.parentAccount });
            return { ...newState };
        }
        case AccountsEliminationsActionTypes.RESET_SETTINGS: {
            const newState = { ...state };
            newState.eliminationsSettings = action.payload.eliminationsSettings;
            newState.indeterminateEliminations = action.payload.indeterminateEliminations;
            return { ...newState };
        }
        default:
            return state;
    }
};

export const TitleBar = styled.div`
    border-left: 1px solid ${COLOR.MAIN.FORM_FIELD};
    background-color: ${COLOR.PRIMARY.WHITE};
    color: ${COLOR.DATAGRID.DATAGRID_TEXT};
    display: flex;
    align-items: center;
    font-size: ${FONT_SIZE.X_SMALL};
    font-weight: 700;
    justify-content: space-between;
    padding: ${SPACING.SMALL};
    min-height: 18px;
`;

const ToggleAccountDetails = styled.button`
    border: 0;
    background-color: transparent;
    color: ${COLOR.DATAGRID.DATAGRID_TEXT};
    cursor: pointer;
    line-height: 12px;
    font-size: inherit;
    border-bottom: 1px solid ${COLOR.PRIMARY.BLACK};
    display: flex;
    align-items: center;
    padding: 0;
`;

const DataGridDataTableContainer = styled.div<{ nonDataColumnsWidth: number; disabled: boolean }>`
    width: calc(100% - ${(props) => props.nonDataColumnsWidth}px);
    position: relative;
    ${(props) => props.disabled && `opacity: 0.2;`};
`;

// flatten the monthly data
const flattenData = (data: MonthlyDataGroupedByKey) => {
    const flatData: MonthlyDataValueLookup = {};
    for (const item in data) {
        for (const monthlyDataPoint of data[item]) {
            flatData[`${item}-${dayjs(monthlyDataPoint.date).format('YYYY-MM')}`] = monthlyDataPoint.value;
        }
    }
    return flatData;
};

const GetCellValue = ({
    isGroup,
    month,
    accountId,
    actualsToDate,
    monthlyData,
    accountName,
    dataId,
}: GetCellValueProps) => {
    const formatString = 'YYYY-MM';
    const monthFormatted = dayjs(month).format(formatString);
    let value = 0;
    const hasVal = typeof monthlyData[`${accountId}-${monthFormatted}`] !== 'undefined';
    if (hasVal) {
        value = monthlyData[`${accountId}-${monthFormatted}`];
    } else {
        value = 0;
    }

    return (
        <AccountCell
            isNumber
            isLocked
            isGroup={isGroup}
            data-test-id={`${dataId}__${camelToKebab(accountName)}__${monthFormatted}`}
            isBudget={month.isAfter(actualsToDate, 'month')}
        >
            {value}
        </AccountCell>
    );
};

/**
 * determines if the account is expanded according to the expansion mode and type of account
 * @param expansionMode
 * @param account
 */
// account is currently unused but should be passed through to make it possible to be used
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isExpanded = (expansionMode: ExpansionMode, account: AccountType | AccountChildrenType): boolean => {
    return expansionMode === ExpansionMode.expanded;
};

const MemoizedGetCellValue = memo(GetCellValue);

export const columnWidths = {
    accounts: 305,
    eliminations: 71,
    reportCode: 142,
    displayName: 156,
    endOfFinancialYearTotal: 130,
};

export const handleToggleAccountRow = (id: string): void => {
    // DOM manipulation to make performance better.
    const dataIds = document.querySelectorAll(`.parent-id-${id}`);
    const rowIcons = document.querySelectorAll(`[data-account="${id}"] .icon--toggle-row`);
    dataIds.forEach((element) => element.classList.toggle('h-display--none'));
    rowIcons.forEach((element) => element.classList.toggle('h-display--none'));
};

/**
 * The interaction mode of the grid
 */
export enum DataGridModes {
    Default = 'DEFAULT',
    EditEliminations = 'EDIT_ELIMINATIONS',
}

const DataGrid: React.FC<React.PropsWithChildren<DataGridProps>> = ({
    data,
    dataId,
    detailsColumns = [],
    gridColumns = [],
    expansionMode = ExpansionMode.expanded,
    displayNameChange,
    isBalanceSheet = false,
    identifyEliminatedAccounts = false,
    hasEliminationsMode = false,
    hasArapAdjustments = false,
    actionHandler,
    ...props
}) => {
    const [showAccountDetails, toggleAccountDetails] = React.useState(false);
    const [gridData, setGridData] = React.useState(data);
    const [dataGridWidth, setDataGridWidth] = React.useState(0);
    const [arrMonths, setArrMonths] = React.useState<Dayjs[]>([]);
    const [monthlyData, setMonthlyData] = React.useState<MonthlyDataValueLookup>(flattenData(data.data));
    const [currentFinancialYear, setCurrentFinancialYear] = React.useState<number>(0);
    const [actualsToDate, setActualsToDate] = React.useState(dayjs(data.organisation.lastActualsDate));
    const [mode, setMode] = React.useState<DataGridModes>(DataGridModes.Default);
    const [accountsEliminationsState, accountsEliminationsDispatch] = React.useReducer(accountsEliminationsReducer, {
        eliminationsSettings: getEliminatedAccounts(gridData.accounts),
        indeterminateEliminations: getIndeterminateAccounts(gridData.accounts),
    });

    const StyledDataGridRef = React.useRef<HTMLDivElement>(null);
    const DataGridScrollerRef = React.useRef<HTMLDivElement>(null);
    const DataGridTableRef = React.useRef<HTMLTableElement>(null);
    const ScrollBarRef = React.useRef<HTMLDivElement>(null);
    const FloatHeadRef = React.useRef<HTMLDivElement>(null);
    const FloatHeadRowRef = React.useRef<HTMLTableRowElement>(null);

    let ticking = false;

    const hasDetailsColumns = detailsColumns.length > 0;
    const hasFYTotal = gridColumns.includes(GridColumns.EndOfFinancialYearTotal);

    const accountsPanelWidth = (() => {
        const accountsWidth = columnWidths.accounts;
        const accountColumnsWidth = mode === DataGridModes.EditEliminations ? columnWidths.eliminations : 0;
        const detailsWidth = showAccountDetails
            ? (() => {
                  let dw = 0;
                  detailsColumns.includes(DetailsColumns.ReportCode) && (dw += columnWidths.reportCode);
                  detailsColumns.includes(DetailsColumns.DisplayName) && (dw += columnWidths.displayName);
                  return dw;
              })()
            : 0;
        return accountsWidth + accountColumnsWidth + detailsWidth;
    })();

    const nonDataColumnsWidth = (() => {
        const gridColumnsWidth = hasFYTotal ? columnWidths.endOfFinancialYearTotal : 0;
        return accountsPanelWidth + gridColumnsWidth;
    })();

    const cells = FloatHeadRef.current && FloatHeadRef.current.querySelectorAll('[data-fy]');

    function update() {
        const DataGridScrollerScrollLeft = DataGridScrollerRef.current && DataGridScrollerRef.current.scrollLeft;
        const DataGridScrollerRects = DataGridScrollerRef.current && DataGridScrollerRef.current.getClientRects();

        if (DataGridScrollerScrollLeft !== null && DataGridScrollerRects !== null && cells !== null && cells.length) {
            const cellNumber =
                DataGridScrollerRects[0] &&
                cells[0] &&
                Math.floor(
                    (DataGridScrollerRects[0].width + DataGridScrollerScrollLeft) /
                        cells[0].getBoundingClientRect().width,
                );
            const cellElement = cells[cellNumber];

            // @ts-ignore
            if (cellElement && currentFinancialYear !== Number(cellElement.dataset.fy)) {
                // @ts-ignore
                setCurrentFinancialYear(Number(cellElement.dataset.fy));
            }
            if (FloatHeadRowRef.current !== null && DataGridScrollerRef.current !== null) {
                if (
                    FloatHeadRowRef.current.getBoundingClientRect().width -
                        DataGridScrollerRef.current.getBoundingClientRect().width ===
                        DataGridScrollerScrollLeft &&
                    cells[cellNumber - 1]
                ) {
                    // @ts-ignore
                    setCurrentFinancialYear(Number(cells[cellNumber - 1].dataset.fy));
                }
            }
        }
        ticking = false;
    }

    function requestTick() {
        if (!ticking) {
            requestAnimationFrame(update);
            ticking = true;
        }
    }

    function handleScroll() {
        requestTick();
    }

    handleScroll();

    const handleDataGridScroll = useDebouncedCallback(() => {
        if (ScrollBarRef.current !== null && DataGridScrollerRef.current !== null && FloatHeadRef.current !== null) {
            const DataGridScrollerScrollLeft = DataGridScrollerRef.current.scrollLeft;
            ScrollBarRef.current.scrollLeft = DataGridScrollerScrollLeft;
            FloatHeadRef.current.scrollLeft = DataGridScrollerScrollLeft;
        }
    }, 10);

    const handleScrollBarScroll = () => {
        if (DataGridScrollerRef.current !== null && ScrollBarRef.current !== null && FloatHeadRef.current !== null) {
            handleScroll();

            DataGridScrollerRef.current.scrollLeft = ScrollBarRef.current.scrollLeft;
            FloatHeadRef.current.scrollLeft = DataGridScrollerRef.current.scrollLeft;
        }
    };

    // update the grid data state when data prop updates
    useEffect(() => {
        setGridData(data);
    }, [data]);

    useEffect(() => {
        const startDate = dayjs(gridData.organisation.startDate);
        const endDate = dayjs(gridData.organisation.endDate);
        // create monthly range between startDate and endDate
        const months: Dayjs[] = [];
        const diffBetween = endDate.diff(startDate, 'month');
        for (let i = 0; i <= diffBetween; i++) {
            months.push(startDate.add(i, 'month'));
        }
        setArrMonths(months);
    }, [gridData.organisation.startDate, gridData.organisation.endDate]);

    useEffect(() => {
        setActualsToDate(dayjs(gridData.organisation.lastActualsDate));
    }, [gridData.organisation.lastActualsDate]);

    useEffect(() => {
        setMonthlyData(flattenData(gridData.data));
    }, [gridData.data]);

    useEffect(() => {
        const getDimensions = () => ({
            width: (DataGridTableRef.current && DataGridTableRef.current.offsetWidth) || 0,
        });
        setDataGridWidth(getDimensions().width);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [DataGridTableRef.current]);

    /* Action handlers */

    const toggleAccountDetailsHandler = () => toggleAccountDetails(!showAccountDetails);

    const editEliminationsHandler = () => {
        // set the mode to eliminations mode
        setMode(DataGridModes.EditEliminations);
    };

    const saveEliminationsHandler = async () => {
        if (typeof actionHandler !== 'function') {
            // set the mode back to default
            setMode(DataGridModes.Default);
            return;
        }
        // save the eliminations settings
        await actionHandler<DataGridExternalActions, { settings: EliminationsSettings }, unknown>({
            type: DataGridExternalActions.SAVE_ELIMINATIONS,
            payload: { settings: accountsEliminationsState.eliminationsSettings },
        });
        // set the mode back to default
        setMode(DataGridModes.Default);
    };

    const cancelEliminationsHandler = () => {
        const eliminationsSettings = getEliminatedAccounts(gridData.accounts);
        const indeterminateEliminations = getIndeterminateAccounts(gridData.accounts);

        accountsEliminationsDispatch({
            type: AccountsEliminationsActionTypes.RESET_SETTINGS,
            payload: {
                eliminationsSettings: eliminationsSettings,
                indeterminateEliminations: indeterminateEliminations,
            },
        });

        setMode(DataGridModes.Default);
    };

    const adjustArapHandler = () => {
        if (typeof actionHandler !== 'function') {
            return;
        }
        actionHandler<DataGridExternalActions, undefined, unknown>({ type: DataGridExternalActions.ADJUST_ARAP });
    };

    return (
        <>
            <DataGridHeader
                dataId={`${dataId}`}
                mode={mode}
                currency={gridData.organisation.currency}
                numAccounts={gridData.accounts.length}
                hasEliminationsMode={hasEliminationsMode}
                hasArapAdjustments={hasArapAdjustments}
                editEliminationsHandler={editEliminationsHandler}
                saveEliminationsHandler={saveEliminationsHandler}
                cancelEliminationsHandler={cancelEliminationsHandler}
                adjustArapHandler={adjustArapHandler}
            />
            <StyledDataGrid {...props} ref={StyledDataGridRef} data-test-id={`${dataId}__grid`}>
                <StyledDataGridInner>
                    <AccountsPanel width={accountsPanelWidth} data-test-id={`${dataId}__accounts-panel`}>
                        <TitleBar>
                            Accounts{' '}
                            {hasDetailsColumns && (
                                <ToggleAccountDetails
                                    data-test-id={`${dataId}__button-toggle-account-details`}
                                    onClick={toggleAccountDetailsHandler}
                                >
                                    {showAccountDetails && <StyledChevronLeft />} {showAccountDetails ? 'Hide' : 'Show'}{' '}
                                    details️ {!showAccountDetails && <StyledChevronRight />}
                                </ToggleAccountDetails>
                            )}
                        </TitleBar>
                        <FloatTableHead>
                            <StyledFloatTable>
                                <AccountHeader>
                                    <tr>
                                        {mode === DataGridModes.EditEliminations && (
                                            <th>
                                                <AccountCell
                                                    hasTextAlignedLeft
                                                    isHeader
                                                    minWidth={columnWidths.eliminations}
                                                >
                                                    Eliminate
                                                </AccountCell>
                                            </th>
                                        )}
                                        <th>
                                            <AccountCell hasTextAlignedLeft isHeader minWidth={columnWidths.accounts}>
                                                Account Name
                                            </AccountCell>
                                        </th>
                                        {detailsColumns.includes(DetailsColumns.ReportCode) && (
                                            <th>
                                                <AccountCell
                                                    hasTextAlignedLeft
                                                    isHeader
                                                    canBeToggled
                                                    isDisplayed={showAccountDetails}
                                                    minWidth={columnWidths.reportCode}
                                                >
                                                    Report Code
                                                </AccountCell>
                                            </th>
                                        )}
                                        {detailsColumns.includes(DetailsColumns.DisplayName) && (
                                            <th>
                                                <AccountCell
                                                    hasTextAlignedLeft
                                                    minWidth={columnWidths.displayName}
                                                    isHeader
                                                    canBeToggled
                                                    isDisplayed={showAccountDetails}
                                                >
                                                    Spotlight Display Name
                                                </AccountCell>
                                            </th>
                                        )}
                                    </tr>
                                </AccountHeader>
                            </StyledFloatTable>
                        </FloatTableHead>
                        <AccountsList
                            dataId={dataId}
                            accountsEliminationsState={accountsEliminationsState}
                            accountsEliminationsDispatch={accountsEliminationsDispatch}
                            accounts={gridData.accounts}
                            detailsColumns={detailsColumns}
                            displayNameChange={displayNameChange}
                            expansionMode={expansionMode}
                            handleToggleAccountRow={handleToggleAccountRow}
                            identifyEliminatedAccounts={identifyEliminatedAccounts}
                            showAccountDetails={showAccountDetails}
                            mode={mode}
                        />
                    </AccountsPanel>
                    <DataGridDataTableContainer
                        nonDataColumnsWidth={nonDataColumnsWidth}
                        disabled={mode === DataGridModes.EditEliminations}
                    >
                        <div>
                            <TitleBar>&nbsp;</TitleBar>
                        </div>
                        <FloatHead ref={FloatHeadRef}>
                            <StyledFloatTable>
                                <thead>
                                    <tr ref={FloatHeadRowRef}>
                                        {arrMonths.map((month: Dayjs) => (
                                            <HeaderCell
                                                key={`month-${month.format('YYYY-MM-DD')}`}
                                                dataId={`${dataId}__data-accounts-table__th-month-${month.format(
                                                    'M-YYYY',
                                                )}`}
                                                month={month}
                                                financialYearStartMonth={gridData.organisation.financialYearStartMonth}
                                            />
                                        ))}
                                    </tr>
                                </thead>
                            </StyledFloatTable>
                        </FloatHead>
                        <DataGridScroller
                            data-test-id={`${dataId}__data-grid-scroller`}
                            ref={DataGridScrollerRef}
                            onScroll={() => handleDataGridScroll()}
                            disabled={mode === DataGridModes.EditEliminations}
                        >
                            <StyledTable ref={DataGridTableRef}>
                                <tbody>
                                    {gridData.accounts.map((account: AccountType) => (
                                        <React.Fragment key={account.id}>
                                            <AccountRow
                                                data-test-id={`${dataId}__tr-${camelToKebab(account.name)}`}
                                                data-account-id={account.id}
                                            >
                                                {arrMonths.map((month: Dayjs) => (
                                                    <td
                                                        key={`cell-${account.id}-${month.format('YYYY-MM')}`}
                                                        data-test-id={`${dataId}__data-accounts-table__td-${month.format(
                                                            'M-YYYY',
                                                        )}-${camelToKebab(account.name)}`}
                                                    >
                                                        <MemoizedGetCellValue
                                                            isGroup={!!account.children.length}
                                                            month={month}
                                                            accountId={account.id}
                                                            actualsToDate={actualsToDate}
                                                            monthlyData={monthlyData}
                                                            accountName={account.name}
                                                            dataId={dataId}
                                                        />
                                                    </td>
                                                ))}
                                            </AccountRow>
                                            {account.children.map((accountChild: AccountChildrenType) => (
                                                <AccountRow
                                                    key={accountChild.id}
                                                    className={classNames(`parent-id-${account.id}`, {
                                                        'h-display--none': !isExpanded(expansionMode, account),
                                                    })}
                                                    data-test-id={`${dataId}__tr-${accountChild.id}`}
                                                >
                                                    {arrMonths.map((month: Dayjs) => (
                                                        <td
                                                            key={`cell-${accountChild.id}-${month.format('YYYY-MM')}`}
                                                            data-test-id={`${dataId}__data-accounts-table__td-${month.format(
                                                                'M-YYYY',
                                                            )}-${camelToKebab(accountChild.name)}-${camelToKebab(
                                                                account.name,
                                                            )}`}
                                                        >
                                                            <MemoizedGetCellValue
                                                                month={month}
                                                                accountId={accountChild.id}
                                                                actualsToDate={actualsToDate}
                                                                monthlyData={monthlyData}
                                                                accountName={`${account.name}__${accountChild.name}`}
                                                                dataId={dataId}
                                                            />
                                                        </td>
                                                    ))}
                                                </AccountRow>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </StyledTable>
                        </DataGridScroller>
                        <StyledScrollbarWrapper
                            data-test-id={`${dataId}__styled-scrollbar-wrapper`}
                            ref={ScrollBarRef}
                            onScroll={() => handleScrollBarScroll()}
                            disabled={mode === DataGridModes.EditEliminations}
                        >
                            <StyledScrollbar dataGridWidth={dataGridWidth} />
                        </StyledScrollbarWrapper>
                    </DataGridDataTableContainer>
                    {hasFYTotal && (
                        <FYTotalColumn
                            dataId={dataId}
                            gridData={gridData}
                            expansionMode={expansionMode}
                            currentFinancialYear={currentFinancialYear}
                            financialYearStartMonth={gridData.organisation.financialYearStartMonth}
                            isBalanceSheet={isBalanceSheet}
                            disabled={mode === DataGridModes.EditEliminations}
                        />
                    )}{' '}
                </StyledDataGridInner>
            </StyledDataGrid>
        </>
    );
};

export { DataGrid };
